import { ball, body, coverBg, edge, fade, gloss, mix, n, shade, star, tint, type ArtPal } from './kit';

/**
 * Neon Arcade: an 80s arcade at night (a pastel dusk in light). The left third stays calm for labels — every focus
 * object sits right of x 213: an upright arcade cabinet with a pink neon marquee and a purple tube along its edges,
 * four reel windows (pixel star · UFO · UFO · crystal), a joystick and two buttons on the panel, a lever with a pink
 * knob on its right; behind it a striped neon sun on a synthwave grid floor, an arcade UFO with a teal beam and
 * pixel twinkles. No casino props (no 7s, cherries, bars, bells, chips or coin piles).
 */
export function neonArcadeCover(P: ArtPal, p: string): string {
  const top = P.dark ? mix(P.bg, P.purple, 0.22) : mix(P.purple, '#FFFFFF', 0.86);
  const mid = P.dark ? mix(P.panel, P.pink, 0.16) : mix(P.pink, '#FFFFFF', 0.84);
  const bottom = P.dark ? mix(P.bg, P.blue, 0.14) : mix(P.sky, '#FFFFFF', 0.86);
  const bg = coverBg(p, P, top, mid, bottom, [420, 200]);
  const neonC = (c: string) => (P.dark ? tint(c, 0.12) : shade(c, 0.05));
  const pink = neonC(P.pink);
  const purple = neonC(P.purple);
  const teal = neonC(P.teal);
  const sky = neonC(P.sky);
  const gold = P.dark ? P.gold : mix(P.gold, '#FFD166', 0.6);
  const cab = P.dark ? mix(P.bg, P.purple, 0.3) : mix(P.purple, P.ink, 0.55);
  const screen = P.dark ? shade(P.bg, 0.3) : '#1B2233';
  const halo = P.dark ? 0.32 : 0.2;
  const floor = P.dark ? mix(P.bg, P.purple, 0.12) : mix(P.purple, '#FFFFFF', 0.9);
  const round = 'stroke-linejoin="round" stroke-linecap="round"';

  /** neon tube: halo, tube, pale core */
  const tube = (d: string, c: string, w = 3) =>
    `<path d="${d}" fill="none" stroke="${c}" stroke-opacity="${halo}" stroke-width="${n(w * 3.2)}" ${round}/>` +
    `<path d="${d}" fill="none" stroke="${c}" stroke-width="${w}" ${round}/>` +
    `<path d="${d}" fill="none" stroke="${tint(c, 0.7)}" stroke-width="${n(w * 0.36)}" ${round}/>`;

  /** pixel twinkle: a plus of three pixels each way */
  const twinkle = (x: number, y: number, c: string, s = 3) =>
    `<path transform="translate(${x} ${y})" d="M-${s / 2} -${s * 2.5}h${s}v${s * 2}h${s * 2}v${s}h-${s * 2}v${s * 2}h-${s}v-${s * 2}h-${s * 2}v-${s}h${s * 2}Z" fill="${c}"/>`;

  // synthwave floor: horizon at y 300, rays from the vanishing point (420, 300), right of the calm third
  const hz = 300;
  let grid = '';
  for (let i = 0; i < 6; i++) {
    const y = hz + 6 + i * i * 4 + i * 6;
    grid += `M240 ${n(y)}H640`;
  }
  for (let i = -4; i <= 8; i++) grid += `M${420 + i * 18} ${hz}L${420 + i * 70} 400`;

  /** neon sun: a disc with horizontal cut stripes (drawn as bars of the sky colour over it) */
  const sun =
    `<circle cx="578" cy="${hz}" r="56" fill="url(#${p}sun)"/>` +
    [0, 1, 2, 3]
      .map((i) => `<rect x="520" y="${hz - 30 + i * 8}" width="120" height="${n(2 + i)}" fill="${mid}"/>`)
      .join('') +
    `<rect y="${hz}" width="640" height="${400 - hz}" fill="${floor}"/>`;

  /** reel window symbols (small): star, ufo, ufo, crystal */
  const mini = {
    star: (x: number) =>
      `<polygon points="${star(x, 196, 11, 4.6, 5)}" fill="url(#${p}gd)" stroke="${edge(gold, P)}" stroke-width="1"/>`,
    ufo: (x: number) =>
      `<g transform="translate(${x} 198)"><path d="M-7 -2C-7 -9 -3 -12 0 -12C3 -12 7 -9 7 -2Z" fill="url(#${p}dm)"/>` +
      `<ellipse cx="0" cy="0" rx="13" ry="5" fill="url(#${p}uf)" stroke="${purple}" stroke-width="1.4"/>` +
      `<circle cx="-6" cy="1" r="1.4" fill="${gold}"/><circle cx="0" cy="2" r="1.4" fill="${gold}"/><circle cx="6" cy="1" r="1.4" fill="${gold}"/></g>`,
    crystal: (x: number) =>
      `<g transform="translate(${x} 196)"><path d="M-11 -4L-6 -11H6L11 -4L0 12Z" fill="url(#${p}cr)" stroke="${teal}" stroke-width="1.4" stroke-linejoin="round"/>` +
      `<path d="M-11 -4H11M-4 -4L0 12L4 -4" fill="none" stroke="${tint(P.teal, 0.6)}" stroke-opacity=".7" stroke-width=".8"/></g>`,
  };

  const cabinet =
    'M336 96H484Q494 96 494 106V150L482 168V262L500 296V360Q500 370 490 370H330Q320 370 320 360V296L338 262V168L326 150V106Q326 96 336 96Z';

  const defs =
    `<defs>${bg.defs}` +
    `<linearGradient id="${p}sun" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="${gold}"/><stop offset=".55" stop-color="${mix(P.orange, P.pink, 0.4)}"/><stop offset="1" stop-color="${P.pink}"/></linearGradient>` +
    body(`${p}cb`, cab, 0.25, 0.4, 0.7, 1) +
    body(`${p}gd`, gold, 0.5, 0.35, 0.5, 1) +
    ball(`${p}dm`, P.sky, 0.6, 0.3) +
    body(`${p}uf`, P.dark ? mix(P.purple, P.bg, 0.3) : tint(P.purple, 0.4), 0.4, 0.4, 0.5, 1) +
    body(`${p}cr`, P.teal, 0.4, 0.4, 0.6, 1) +
    ball(`${p}kn`, P.pink, 0.55, 0.4) +
    ball(`${p}js`, P.red, 0.55, 0.4) +
    fade(`${p}gl`, P.pink, P.dark ? 0.45 : 0.3) +
    fade(`${p}bm`, P.teal, P.dark ? 0.5 : 0.35) +
    fade(`${p}sh`, P.dark ? '#000000' : shade(P.ink, 0.2), P.dark ? 0.6 : 0.22) +
    `</defs>`;

  return (
    defs +
    bg.body +
    sun +
    `<path d="M240 ${hz}H640" stroke="${pink}" stroke-width="2.4"/>` +
    `<path d="${grid}" fill="none" stroke="${purple}" stroke-opacity="${P.dark ? 0.55 : 0.45}" stroke-width="1.6"/>` +
    // glow behind the cabinet and its shadow
    `<ellipse cx="410" cy="220" rx="150" ry="150" fill="url(#${p}gl)"/>` +
    `<ellipse cx="410" cy="372" rx="120" ry="10" fill="url(#${p}sh)"/>` +
    // cabinet body, marquee, screen
    `<path d="${cabinet}" fill="url(#${p}cb)" stroke="${edge(cab, P)}" stroke-width="2"/>` +
    tube(cabinet, purple, 3) +
    `<rect x="340" y="108" width="140" height="34" rx="6" fill="${screen}"/>` +
    tube('M346 114H474V136H346Z', pink, 2.6) +
    twinkle(372, 125, sky, 2) +
    twinkle(448, 125, gold, 2) +
    `<rect x="350" y="174" width="120" height="46" rx="6" fill="${screen}" stroke="url(#${p}gd)" stroke-width="3"/>` +
    [353, 382, 411, 440]
      .map(
        (x) =>
          `<rect x="${x}" y="178" width="27" height="38" rx="3" fill="${P.dark ? '#111A2A' : '#F7F9FC'}"/>`,
      )
      .join('') +
    mini.star(366.5) +
    mini.ufo(395.5) +
    mini.ufo(424.5) +
    mini.crystal(453.5) +
    `<path d="M352 197H468" stroke="${pink}" stroke-opacity=".55" stroke-width="1.4"/>` +
    // control panel: joystick and two buttons
    `<path d="M338 262H482L500 296H320Z" fill="${shade(cab, 0.25)}" stroke="${teal}" stroke-width="2"/>` +
    `<path d="M370 282L366 252" stroke="${P.dark ? '#DDE4F0' : '#B9C2D2'}" stroke-width="4" stroke-linecap="round"/>` +
    `<circle cx="365" cy="248" r="9" fill="url(#${p}js)" stroke="${edge(P.red, P)}" stroke-width="1.6"/>` +
    gloss(362, 245, 3, 2, -35, 0.8) +
    `<ellipse cx="430" cy="281" rx="7" ry="3.6" fill="${sky}"/><ellipse cx="452" cy="281" rx="7" ry="3.6" fill="${gold}"/>` +
    // lower panel stripes
    `<path d="M336 318H484M336 334H484" stroke="${sky}" stroke-opacity=".7" stroke-width="2.4"/>` +
    gloss(344, 190, 6, 50, 0, 0.1) +
    // lever on the right side
    `<path d="M500 240L536 150" stroke="${edge(gold, P)}" stroke-width="11" stroke-linecap="round"/>` +
    `<path d="M500 240L536 150" stroke="url(#${p}gd)" stroke-width="7" stroke-linecap="round"/>` +
    `<rect x="494" y="226" width="16" height="30" rx="6" fill="url(#${p}gd)" stroke="${edge(gold, P)}" stroke-width="1.6"/>` +
    `<circle cx="538" cy="142" r="17" fill="url(#${p}kn)" stroke="${edge(P.pink, P)}" stroke-width="2"/>` +
    gloss(532, 135, 5, 3.4, -35, 0.75) +
    // UFO with a beam, top right
    `<g transform="translate(590 70)">` +
    `<path d="M-10 8L-22 70H22L10 8Z" fill="url(#${p}bm)"/>` +
    `<path d="M-12 0C-12 -12 -6 -18 0 -18C6 -18 12 -12 12 0Z" fill="url(#${p}dm)"/>` +
    `<ellipse cx="0" cy="2" rx="28" ry="9" fill="url(#${p}uf)"/>` +
    tube('M-28 2C-28 -4 -14 -7 0 -7C14 -7 28 -4 28 2C28 8 14 11 0 11C-14 11 -28 8 -28 2Z', purple, 2) +
    `<circle cx="-14" cy="4" r="2" fill="${gold}"/><circle cx="0" cy="6" r="2" fill="${gold}"/><circle cx="14" cy="4" r="2" fill="${gold}"/>` +
    `</g>` +
    twinkle(262, 60, sky) +
    twinkle(300, 150, pink, 2) +
    twinkle(520, 40, gold) +
    twinkle(624, 200, teal, 2) +
    twinkle(250, 230, gold, 2)
  );
}
